import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, CircularProgress, TextField } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { parseClientDetailsFormConfig } from "utils/clientDetailsFormConfig";
import ClientDetailsFormFields from "views/admin/Settings/ClientDetailsForm/ClientDetailsFormFields";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchLeadForm, saveLeadForm } from "../../api/endpoints/leads";
import { EmptyState, PageHeader, Panel, StatusChip } from "../../components/ui";
import { canEditLead, leadStateTone } from "./leadHelpers";
import { buildFormValues, mergeUnlistedAnswers, unlistedAnswers } from "./leadFormHelpers";

const CHIP_TONES = {
  default: "neutral",
  info: "sky",
  warning: "amber",
  success: "emerald",
  error: "rose",
};

const LeadFormPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(
    partnerQueryKeys.leadForm(leadId),
    () => fetchLeadForm(leadId),
    { enabled: Boolean(leadId), refetchOnMount: true }
  );

  const fields = useMemo(
    () => parseClientDetailsFormConfig(data?.fieldsJson).filter((field) => field.enabled),
    [data?.fieldsJson]
  );

  const [values, setValues] = useState({});
  const [extras, setExtras] = useState([]);

  useEffect(() => {
    if (!data) return;
    setValues(buildFormValues(fields, data.values));
    setExtras(unlistedAnswers(fields, data.values));
  }, [data, fields]);

  const readOnly = data ? !canEditLead({ state: data.state }) : true;

  const saveMutation = useMutation((payload) => saveLeadForm(leadId, payload), {
    onSuccess: () => {
      toast.success("Form saved");
      queryClient.invalidateQueries(partnerQueryKeys.leadForm(leadId));
      queryClient.invalidateQueries(partnerQueryKeys.leads());
      navigate("/partner/clients?tab=leads");
    },
    onError: (err) =>
      toast.error(
        err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not save"
      ),
  });

  const handleChange = (id, value) => setValues((prev) => ({ ...prev, [id]: value }));

  const handleExtraChange = (key, value) =>
    setExtras((prev) => prev.map((item) => (item.key === key ? { ...item, value } : item)));

  const handleSubmit = (event) => {
    event.preventDefault();
    saveMutation.mutate(mergeUnlistedAnswers(values, extras));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        title="Couldn't load this form"
        description="The lead may have been removed. Go back and try again."
        action={
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate("/partner/clients?tab=leads")}
          >
            Back to leads
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <PageHeader
        title={values.name || "Client details"}
        actions={
          <div className="flex items-center gap-3">
            <StatusChip tone={CHIP_TONES[leadStateTone(data.state)] || "neutral"}>
              {data.state}
            </StatusChip>
            <Link
              to="/partner/clients?tab=leads"
              className="inline-flex items-center gap-1 text-sm text-teal-700 no-underline hover:underline"
            >
              <ArrowBackIcon fontSize="small" /> Leads
            </Link>
          </div>
        }
      />

      <form onSubmit={handleSubmit}>
        <Panel title="Client details">
          <ClientDetailsFormFields
            fields={fields}
            values={values}
            onChange={handleChange}
            disabled={readOnly}
          />
        </Panel>

        {extras.length ? (
          <div className="mt-6">
            <Panel title="Answers to fields no longer on the form">
              <p className="text-xs text-slate-500 m-0 mb-4">
                These were answered before the form changed. They stay on the record unless you
                clear them.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {extras.map((item) => (
                  <TextField
                    key={item.key}
                    fullWidth
                    size="small"
                    label={item.label}
                    value={item.value}
                    disabled={readOnly}
                    helperText={item.wasList ? "Separate several answers with commas" : undefined}
                    onChange={(event) => handleExtraChange(item.key, event.target.value)}
                  />
                ))}
              </div>
            </Panel>
          </div>
        ) : null}

        {readOnly ? null : (
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outlined" onClick={() => navigate("/partner/clients?tab=leads")}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saveMutation.isLoading}>
              {saveMutation.isLoading ? "Saving…" : "Save form"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default LeadFormPage;
